import axiosClient from '../../../api/axiosClient.js';
import { fetchPageData } from '../../../api/utils.js';

export async function createGameSession({game, user}) {
  const body = { game, user, level1: 'not_started', is_ready: true };
  try {
    const { data } = await axiosClient.post(`/api/games/sessions/`, body);
    return data;
  } catch (err) {
    // BE partial unique index on (user, game) WHERE completed=false can surface as IntegrityError (500) on an inter-request race; one retry picks up the just-created row via the serializer's reuse path.
    if (err?.response?.status === 500) {
      const { data } = await axiosClient.post(`/api/games/sessions/`, body);
      return data;
    }
    throw err;
  }
}

async function getCourses() {
  return fetchPageData(`/api/users/courses/`);
}

export const coursesQuery = () => {
  return {
    queryKey: ['courses'],
    queryFn: async () => getCourses(),
    refetchOnWindowFocus: false
  };
};

export async function updateCourse({id, description, businessType}) {
  const payload = {};
  if (description) payload.description = description;
  if (businessType) payload.businesstype = businessType;

  const { data } = await axiosClient.patch(`/api/users/courses/${id}/`, payload);

  return data;
}

export async function createScenario({
  name,
  courses
}) {
  const { data } = await axiosClient.post(`api/games/scenarios/`, {
    name,
    is_published: true,
    courses
  });

  return data;
}

export async function scenarioAddCourse({scenarioId, courseId}) {
  const { data } = await axiosClient.put(`api/games/scenarios/${scenarioId}/add-course/`, { course_id: courseId });

  return data;
}

export async function scenarioBulkAddCourse({scenarioIds, courseId}) {
  const { data } = await axiosClient.post(`api/games/scenarios/bulk-add-course/`, {
    course_id: courseId,
    scenario_ids: scenarioIds
  });

  return data;
}

export async function createGame({
  name,
  description,
  startTime,
  endTime,
  gameType,
  scenarioId,
  courseId
}) {
  const gameTypeMapping = {
    'classification': 'ACC',
    'normal': 'NOR',
    'equation': 'BE',
    'principles': 'PR',
    'accounting cycle': 'AC',
  };
  
  const gameTypeValue = gameTypeMapping[gameType] || '';
  const { data } = await axiosClient.post(`api/games/games/create/`, {
    name,
    description,
    start_time: startTime,
    end_time: endTime,
    points: 100,
    is_active: true,
    game_type: gameTypeValue,
    scenario: scenarioId,
    course: courseId
  });

  return data;
}

async function getCourseSetup() {
  const response = await axiosClient.get(`/api/games/scenario-groups/course-setup/`);
  return response.data.scenarios;
}

export const coursesSetupQuery = () => {
  return {
    queryKey: ['courseSetup'],
    queryFn: async () => getCourseSetup(),
    refetchOnWindowFocus: false
  };
};

export async function createScenarioLevels({
  name,
  levelNumber,
  scenarios,
  description,
  design
}) {
  const { data } = await axiosClient.post(`api/games/scenario-levels/`, {
    name,
    falling_speed: 2,
    level: levelNumber,
    show_score: true,
    show_value: true,
    scenarios,
    description,
    design
  });

  return data;
}

async function getGamesItems() {
  return fetchPageData(`/api/games/items/`);
}

export const gamesItemsQuery = () => {
  return {
    queryKey: ['gamesItems'],
    queryFn: async () => getGamesItems(),
    refetchOnWindowFocus: false,
    cacheTime: 0
  };
};

export async function createItemQuestionsBulk({
  gameType,
  questions,
  scenarioLevelId
}) {
  const lastSlugPart = gameType === 'classification'
    ? 'item-questions'
    : gameType === 'principles'
      ? 'principles-questions'
      : 'questions';
  const { data } = await axiosClient.post(`api/${gameType}/${lastSlugPart}/bulk/`,
    questions.map(q => ({
      order: q.order,
      ...(gameType === 'equation' || gameType === 'principles' ? { transaction: q.accountItemId } : { account_item: q.accountItemId }),
      scenario_level: scenarioLevelId
    }))
  );

  return data;
}

export async function createItemOptionsBulk({
  gameType,
  options,
  scenarioLevelId
}) {
  const lastSlugPart = gameType === 'classification' ? 'item-options' : 'options';
  const { data } = await axiosClient.post(`api/${gameType}/${lastSlugPart}/bulk/`,
    options.map(opt => ({
      order: opt.order,
      account_item: opt.accountItemId,
      scenario_level: scenarioLevelId
    }))
  );

  return data;
}

async function getScenarioLevels({scenarioId}) {  
  return scenarioId ? fetchPageData(`/api/games/scenario-levels/?scenarios=${scenarioId}`) : [];
}

export const scenarioLevelsQuery = ({scenarioId}) => {
  return {
    queryKey: [`gameScenarioLevels`, scenarioId],
    queryFn: async () => getScenarioLevels({scenarioId}),
    refetchOnWindowFocus: false,
    cacheTime: 0
  };
};